import React, { useState } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import useAuthStore from "store/authStore";
import { BASE_URL } from "service";

export default function ContactUs() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    query: "",
  });
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [loading, setLoading] = useState(false); // Track API loading state

  const { email } = useAuthStore(); // Fetch email from Zustand store

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate email presence
    if (!email) {
      setNotification({
        open: true,
        message: "Please log in to submit a query.",
        severity: "error",
      });
      return;
    }

    setLoading(true); // Start loader

    try {
      const response = await axios.post(
        `${BASE_URL}/api/supportTicket/submit`,
        {
          name: `${formData.firstName} ${formData.lastName}`.trim(),
          email, // Use email from store
          query: formData.query,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      setNotification({
        open: true,
        message: `Query submitted successfully! Ticket ID: ${response.data.ticketId}`,
        severity: "success",
      });

      // Reset form
      setFormData({
        firstName: "",
        lastName: "",
        phoneNumber: "",
        query: "",
      });
    } catch (error) {
      console.error("Error submitting query:", error);
      setNotification({
        open: true,
        message: error.response?.data?.message || "Failed to submit query. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false); // Stop loader
    }
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  return (
    <Box
      sx={{
        backgroundImage:
          "linear-gradient(to right, rgb(183, 188, 208), #e0e0e0, rgb(225, 207, 207))",
        minHeight: "100vh",
        padding: "2rem",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 4,
          maxWidth: 800,
          mx: "auto",
        }}
      >
        <Typography variant="h4" fontWeight="600" align="center" gutterBottom>
          Contact us
        </Typography>
        <Typography variant="subtitle1" align="center" gutterBottom>
          <b>Kamai Club Contact Support!</b>
        </Typography>

        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 4 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First name"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last name"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                label="Phone number"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                fullWidth
                placeholder="123-456-7890"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Message"
                name="query"
                value={formData.query}
                onChange={handleChange}
                multiline
                minRows={4}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={loading} // Disable button while loading
                sx={{
                  py: 1.5,
                  color: "#000000 !important", // Ensure black text
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Let's Talk"}
              </Button>
            </Grid>
          </Grid>
        </Box>

        <Snackbar
          open={notification.open}
          autoHideDuration={6000}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseNotification}
            severity={notification.severity}
            sx={{ width: "100%" }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
}
